<!--
// app/components/pages/region-home/GridNewsHero.vue
-->
<script setup lang="ts">
import type { PostsResponse } from "~/src/post/domain/models/Post";
import postUiService from "~/src/post/infrastructure/ui-services/postUiService";
import regionUiService from "~/src/region/infrastructure/ui-services/regionUiService";
import { setAliasUrl } from "~/src/Shared/utils";
import { useRegions } from "~/app/composables/regions";
import BaseNuxtLink from "~/app/components/base/baseNuxtLink.vue";
import ByExternalMedia from "~/app/components/Shared/news/ByExternalMedia.vue";
import { useWebContext } from "~/app/composables/webContext";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useWebappStore } from "~/app/stores/webappStore";
import EmbedTwitch from "~/app/components/Shared/twitch/EmbedTwitch.vue";

type Props = {
  seeMoreButton?: boolean;
  isNewsPage?: boolean;
};

const route = useRoute();
const props = defineProps<Props>();
const { locale } = useI18n();
const localePath = useLocalePath();
const { getFeaturedPosts, isExternal } = postUiService();
const { isRegionHome, isSpain, isAmericas } = useRegions();
const { isKings, isQueens } = useWebContext();
const competitionStore = useCompetitionStore();
const webappStore = useWebappStore();
const { findRegionInfoBySlug } = regionUiService();

const isMobile = ref<boolean | undefined>();
const seenRegion = ref<string | undefined>(competitionStore.region?.name);

const activeNew = ref(0);
const posts = ref<PostsResponse | undefined>();

const gridKey = ref(0);

posts.value = await getFeaturedPosts(locale.value);
gridKey.value++;

const [firstPost, secondPost, thirdPost] = !!posts.value?.data ? posts.value.data : [];

onMounted(async () => {
  isMobile.value = window.innerWidth < 1025;
  window.addEventListener("resize", () => {
    isMobile.value = window.innerWidth < 1025;
  });

  seenRegion.value = findRegionInfoBySlug(webappStore.twitchInfo?.stream.region, "name");
});

const showTwitchEmbed = computed(() => {
  return (
    !!webappStore.twitchInfo && webappStore.twitchInfo.live && isMobile.value && !("news" in route.params)
  );
});

const showNewsGrid = computed(() => {
  return props.isNewsPage
    ? !!posts.value?.data && posts.value?.data.length && posts.value.data.length >= 1
    : !!posts.value?.data && posts.value?.data.length && posts.value.data.length === 3;
});
</script>

<template>
  <section v-if="(posts?.data && showNewsGrid) || showTwitchEmbed" :key="gridKey">
    <div class="posts-grid-section kql-w-container">
      <div v-if="showTwitchEmbed" class="relative h-full w-full posts">
        <div
          v-if="seenRegion && seenRegion !== 'Spain'"
          class="absolute top-1 left-1 py-1 px-2 bg-gray-700 text-xs font-bold rounded-lg"
        >
          {{ seenRegion }}
        </div>
        <embed-twitch
          :channel="webappStore.twitchInfo?.stream.account"
          width="100%"
          :height="242"
          :is-live="webappStore.twitchInfo?.live"
        />
      </div>
      <nuxt-link
        v-for="(post, i) in showTwitchEmbed && !!posts?.data
          ? [firstPost, secondPost].filter(Boolean)
          : !!posts
            ? [firstPost, secondPost, thirdPost].filter(Boolean)
            : []"
        :key="post.id"
        :to="
          isExternal(post.categories)
            ? post.acf.external_url
            : localePath({
                name: 'region-news-post',
                params: { region: route.params.region, news: $t('general.news.word'), post: post.slug },
              })
        "
        :target="post.target"
        :class="[{ 'is-active': activeNew === i }, 'posts']"
        :no-prefetch="!!isRegionHome"
        @mouseover="activeNew = i"
      >
        <div class="relative h-full w-full">
          <nuxtImg
            :src="post.seoHeadJSON.og_image ? setAliasUrl(post.seoHeadJSON.og_image[0].url) : ''"
            :alt="post.title"
            width="1672"
            height="940"
            sizes="375px sm:430px md:836px"
            quality="70"
            class="post-image"
          />
          <div class="text-gradient" />
          <div class="container-text">
            <by-external-media
              v-if="isExternal(post.categories) && !!post.acf.external_logo_url"
              :logo_url="post.acf.external_logo_url"
            />
            <p class="posts-date">
              <nuxt-time
                :datetime="post.dateGmt"
                month="long"
                day="numeric"
                year="numeric"
                :locale="locale"
              />
            </p>

            <p class="posts-title" v-html="post.title" />
          </div>
        </div>
      </nuxt-link>
    </div>
  </section>
  <div v-if="seeMoreButton && showNewsGrid && !!posts?.data" class="py-6 flex justify-center items-center">
    <base-nuxt-link
      :to="
        localePath({
          name: 'region-news',
          params: { region: route.params.region, news: $t('general.news.word') },
        })
      "
      text-size="text-xs"
      primary
      :no-prefetch="!!isRegionHome"
    >
      {{ $t("news.post.viewAllNews") }}
    </base-nuxt-link>
  </div>
</template>

<style scoped>
.posts-grid-section {
  @apply grid grid-cols-2 md:flex md:justify-center md:items-center md:h-[470px] overflow-hidden;
}

.posts {
  @apply bg-center bg-no-repeat bg-cover flex flex-col justify-end overflow-hidden md:w-1/5 md:h-full md:first:h-full h-[242px] first:col-span-2 md:transition-[width] md:ease-in-out md:duration-500;
}

.posts .post-image {
  @apply absolute top-0 left-0 bottom-0 right-0 w-full h-full object-cover md:transition-transform md:ease-in-out md:duration-500;
}

.posts .posts-date {
  @apply text-[10px] font-normal uppercase mb-2 lg:text-xs;
}

.posts .posts-title {
  @apply text-sm font-normal leading-[17.5px] text-gray-200 md:text-lg md:leading-snug md:font-medium md:text-white;
}

.posts .container-text {
  @apply px-3 pt-3 pb-7 absolute bottom-0 md:pb-14 md:px-6 md:max-w-max md:duration-500 md:ease-in-out;
}

.posts .text-gradient {
  @apply h-full w-full absolute top-0 right-0 bottom-0 left-0;
  background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.9) 10%, rgba(0, 0, 0, 0) 70%);
}

.posts.is-active {
  @apply md:w-3/5;
}

.posts.is-active .container-text {
  @apply md:scale-150 md:origin-bottom-left rtl:md:origin-bottom-right md:max-w-xs lg:max-w-md md:ps-14 md:pe-0;
}

.posts.is-active .posts-date {
  @apply md:text-[10px];
}

.posts.is-active .posts-title {
  @apply md:font-semibold md:leading-[23px];
}
</style>
